import React, { useState, lazy, Suspense, useEffect, useMemo } from 'react';
import type { RarityPriceDataPoint } from '../../api/analytics';
import { getRarityPriceData } from '../../api/analytics';
import '../styles/RarityPriceChart.css';

// Lazy load recharts to reduce initial bundle size
const LazyChart = lazy(() => import('./RarityPriceScatterChartImpl'));

export interface RarityPriceScatterChartProps {
  collectionId: string;
  collectionSize?: number; // Total NFTs in collection for percentage calculation
  className?: string;
}

type RarityFilter = '10%' | '25%' | 'all';

const LoadingSkeleton = () => (
  <div className="rarity-price-chart-skeleton">
    <div className="skeleton-header"></div>
    <div className="skeleton-chart"></div>
    <div className="skeleton-legend"></div>
  </div>
);

export const RarityPriceScatterChart: React.FC<RarityPriceScatterChartProps> = ({
  collectionId,
  collectionSize,
  className = '',
}) => {
  const [data, setData] = useState<RarityPriceDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<{ totalListed: number; totalWithRarity: number } | null>(null);
  // Determine initial filter based on collection size
  const getInitialFilter = (size?: number): RarityFilter => {
    if (!size || size >= 2000) return '10%';
    if (size >= 1000) return '25%';
    return 'all';
  };

  const [rarityFilter, setRarityFilter] = useState<RarityFilter>(getInitialFilter(collectionSize));

  // Update filter when collection size becomes available
  useEffect(() => {
    if (collectionSize) {
      setRarityFilter(getInitialFilter(collectionSize));
    }
  }, [collectionSize]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await getRarityPriceData(collectionId);
        setData(result.dataPoints);
        setStats({
          totalListed: result.totalListed,
          totalWithRarity: result.totalWithRarity,
        });
      } catch (err) {
        console.error('Failed to fetch rarity-price data:', err);
        setError('Failed to load rarity vs price data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [collectionId]);

  // Calculate max rank based on collection size or max rank in data
  const maxRankInCollection = useMemo(() => {
    if (collectionSize && collectionSize > 0) return collectionSize;
    if (data.length === 0) return 100;
    return Math.max(...data.map(d => d.rarityRank));
  }, [collectionSize, data]);

  // Filter data based on rarity percentage
  const filteredData = useMemo(() => {
    if (rarityFilter === 'all') return data;

    const percentageMap: Record<RarityFilter, number> = {
      '10%': 0.10,
      '25%': 0.25,
      'all': 1,
    };

    const maxRank = Math.ceil(maxRankInCollection * percentageMap[rarityFilter]);
    return data.filter(d => d.rarityRank <= maxRank);
  }, [data, rarityFilter, maxRankInCollection]);

  // Count for display
  const displayCount = filteredData.length;

  return (
    <div className={`rarity-price-chart ${className}`}>
      {/* Header */}
      <div className="rarity-price-chart-header">
        <div className="rarity-price-chart-header-row">
          <div className="rarity-price-chart-title-section">
            <div className="rarity-price-chart-title-row">
              <h3 className="rarity-price-chart-title">Rarity vs Price</h3>
              {stats && displayCount > 0 && (
                <span className="rarity-price-chart-count">
                  {displayCount} listed
                </span>
              )}
            </div>
            <p className="rarity-price-chart-subtitle">
              Find potential deals on rare NFTs
            </p>
          </div>
          {/* Rarity Filter Controls */}
          {!loading && !error && data.length > 0 && (
            <div className="rarity-price-chart-controls">
              {(['10%', '25%', 'all'] as RarityFilter[]).map((filter) => (
                <button
                  key={filter}
                  className={`control-button ${rarityFilter === filter ? 'active' : ''}`}
                  onClick={() => setRarityFilter(filter)}
                >
                  {filter === 'all' ? 'All' : `Top ${filter}`}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>


      {/* Chart Content */}
      <div className="rarity-price-chart-content">
        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <div className="rarity-price-chart-error">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="error-icon">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="error-message">{error}</p>
          </div>
        ) : data.length === 0 ? (
          <div className="rarity-price-chart-empty">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="empty-icon">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
            </svg>
            <p>No listed NFTs with rarity data</p>
            <p className="empty-hint">This chart shows NFTs that are currently for sale and have rarity rankings.</p>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="rarity-price-chart-empty">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="empty-icon">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p>No NFTs in the top {rarityFilter} are currently listed</p>
            <p className="empty-hint">Try selecting a larger rarity range.</p>
          </div>
        ) : (
          <Suspense fallback={<LoadingSkeleton />}>
            <LazyChart data={filteredData} maxRank={maxRankInCollection} />
          </Suspense>
        )}
      </div>

      {/* Legend */}
      {!loading && !error && filteredData.length > 0 && (
        <div className="rarity-price-chart-legend">
          <div className="legend-item">
            <div className="legend-dot" style={{ backgroundColor: '#10b981' }} />
            <span>Listed NFTs</span>
          </div>
          <div className="legend-item">
            <div className="legend-band" />
            <span>Fair price range</span>
          </div>
          <div className="legend-hint">
            Points below the band may be underpriced
          </div>
        </div>
      )}
    </div>
  );
};
